import { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Config } from 'atoms/Ltool/newConfigAtoms';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { fetchCalculations, fetchCalculationSet, fetchDestinations, fetchLots, fetchPhases, fetchProjects } from 'api/fetchLtool';
import { naturalSort } from 'utils/sort';
import { useApiErrorNotifier } from 'utils/hooks/useApiErrorNotifier';
import type { CalculationSetData } from 'api/fetchLtool';

// 階層一覧は powertrain 別 URL（/assess/<pt>/...）。グローバルモード powertrainAtom に追従して取得する。
type Loadable<T> = {
    items: T[];
    loading: boolean;
};

// 親（authToken / powertrain / 上位 id）をまとめた識別キー。JSON 化で区切り衝突を避ける。
const keyOf = (...parts: string[]): string => JSON.stringify(parts);

/**
 * 親キー単位で階層一覧を取得する共通フック。
 * 親が変わった瞬間（key 変化〜再 fetch 完了まで）は items を空・loading 扱いにして、
 * 古い親の単一候補が useAutoSelectSingle に拾われる race を防ぐ（stale 破棄）。
 * ready=false（親未選択）のときは常に空・非 loading。
 */
function useKeyedList(
    ready: boolean,
    key: string,
    fetcher: () => Promise<string[]>,
): Loadable<string> {
    const effectiveKey = ready ? key : '';
    const [state, setState] = useState<{ key: string; items: string[] }>({ key: '', items: [] });
    const [loading, setLoading] = useState<boolean>(true);
    const notifyError = useApiErrorNotifier();

    useEffect(() => {
        if (!ready) {
            setState({ key: '', items: [] });
            setLoading(false);
            return;
        }
        setLoading(true);
        fetcher()
            .then(data => setState({ key: effectiveKey, items: naturalSort(data) }))
            .catch(notifyError)
            .finally(() => setLoading(false));
        // fetcher は effectiveKey（authToken / powertrain / 親 id を内包）が変われば作り直される。
        // effectiveKey を依存にすることで親変更時だけ再取得する（毎レンダー実行を避けるため fetcher は除外）。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, effectiveKey, notifyError]);

    const stale = state.key !== effectiveKey;
    return { items: stale ? [] : state.items, loading: loading || stale };
}

export function useProjects(authToken: string): Loadable<string> {
    const powertrain = useAtomValue(powertrainAtom);
    return useKeyedList(true, keyOf(authToken, powertrain), () => fetchProjects(authToken, powertrain));
}

export function useDestinations(authToken: string, config: Config): Loadable<string> {
    const powertrain = useAtomValue(powertrainAtom);
    return useKeyedList(
        !!config.project_id,
        keyOf(authToken, powertrain, config.project_id),
        () => fetchDestinations(authToken, config, powertrain),
    );
}

export function useLots(authToken: string, config: Config): Loadable<string> {
    const powertrain = useAtomValue(powertrainAtom);
    return useKeyedList(
        !!(config.project_id && config.destination_id),
        keyOf(authToken, powertrain, config.project_id, config.destination_id),
        () => fetchLots(authToken, config, powertrain),
    );
}

export function usePhases(authToken: string, config: Config): Loadable<string> {
    const powertrain = useAtomValue(powertrainAtom);
    return useKeyedList(
        !!config.lot_id,
        keyOf(authToken, powertrain, config.project_id, config.destination_id, config.lot_id),
        () => fetchPhases(authToken, config, powertrain),
    );
}

export function useCalculations(authToken: string, config: Config): Loadable<string> {
    const powertrain = useAtomValue(powertrainAtom);
    return useKeyedList(
        !!config.phase_id,
        keyOf(authToken, powertrain, config.project_id, config.destination_id, config.lot_id, config.phase_id),
        () => fetchCalculations(authToken, config, powertrain),
    );
}

export function useCalculationSet(
    authToken: string,
    config: Config
): { data: CalculationSetData | null; loading: boolean } {
    const [data, setData] = useState<CalculationSetData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const powertrain = useAtomValue(powertrainAtom);
    const notifyError = useApiErrorNotifier();

    useEffect(() => {
        if (!config.calculation_id) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchCalculationSet(authToken, config, powertrain)
            .then(d => setData(d))
            .catch(notifyError)
            .finally(() => setLoading(false));
    }, [authToken, config, powertrain, notifyError]);

    return { data, loading };
};
